import { useReducer } from 'react'
import { CONTRAST_STEP, MAX_CONTRAST, MIN_CONTRAST } from './constants'

export type Rotation = 0 | 90 | 180 | 270

export interface IImageAdjustments {
  flipped: boolean
  inverted: boolean
  rotation: Rotation
  contrast: number
}

export type ImageAdjustmentAction =
  | 'toggleFlip'
  | 'toggleInvert'
  | 'rotateLeft'
  | 'rotateRight'
  | 'increaseContrast'
  | 'decreaseContrast'
  | 'reset'

export const INITIAL_ADJUSTMENTS: IImageAdjustments = {
  flipped: false,
  inverted: false,
  rotation: 0,
  contrast: 1
}

const ROTATIONS: Rotation[] = [0, 90, 180, 270]

const rotate = (rotation: Rotation, quarterTurns: 1 | -1): Rotation =>
  ROTATIONS[(ROTATIONS.indexOf(rotation) + quarterTurns + 4) % 4]

// Arredonda para evitar ruído de ponto flutuante a cada degrau (0.1 + 0.2).
const stepContrast = (contrast: number, direction: 1 | -1) =>
  Math.min(
    MAX_CONTRAST,
    Math.max(
      MIN_CONTRAST,
      Math.round((contrast + direction * CONTRAST_STEP) * 100) / 100
    )
  )

export const imageAdjustmentsReducer = (
  state: IImageAdjustments,
  action: ImageAdjustmentAction
): IImageAdjustments => {
  switch (action) {
    case 'toggleFlip':
      return { ...state, flipped: !state.flipped }
    case 'toggleInvert':
      return { ...state, inverted: !state.inverted }
    case 'rotateLeft':
      return { ...state, rotation: rotate(state.rotation, -1) }
    case 'rotateRight':
      return { ...state, rotation: rotate(state.rotation, 1) }
    case 'increaseContrast':
      return { ...state, contrast: stepContrast(state.contrast, 1) }
    case 'decreaseContrast':
      return { ...state, contrast: stepContrast(state.contrast, -1) }
    case 'reset':
      return INITIAL_ADJUSTMENTS
  }
}

export const isAdjusted = (adjustments: IImageAdjustments) =>
  adjustments.flipped !== INITIAL_ADJUSTMENTS.flipped ||
  adjustments.inverted !== INITIAL_ADJUSTMENTS.inverted ||
  adjustments.rotation !== INITIAL_ADJUSTMENTS.rotation ||
  adjustments.contrast !== INITIAL_ADJUSTMENTS.contrast

/** Filtro da imagem; não vale para o overlay, para manter as cores dos destaques. */
export const getAdjustmentFilter = ({
  contrast,
  inverted
}: IImageAdjustments) => {
  const filters = [
    contrast === 1 ? null : `contrast(${contrast})`,
    inverted ? 'invert(1)' : null
  ].filter(Boolean)
  return filters.length > 0 ? filters.join(' ') : 'none'
}

/**
 * Transform da imagem e do overlay juntos (vazio sem ajuste). A rotação vem antes do espelho,
 * então o espelho é sempre no eixo horizontal da própria imagem, qualquer que seja a rotação.
 */
export const getAdjustmentTransform = ({
  rotation,
  flipped
}: IImageAdjustments) => {
  const transforms = [
    rotation === 0 ? null : `rotate(${rotation}deg)`,
    flipped ? 'scaleX(-1)' : null
  ].filter(Boolean)
  return transforms.join(' ')
}

/**
 * Ajustes visuais da imagem (espelho, rotação, contraste e inversão), só em memória: valem
 * enquanto o componente está montado. As ações ficam expostas para atalhos de teclado.
 */
const useImageAdjustments = () => {
  const [adjustments, dispatch] = useReducer(
    imageAdjustmentsReducer,
    INITIAL_ADJUSTMENTS
  )

  return {
    adjustments,
    isAdjusted: isAdjusted(adjustments),
    toggleFlip: () => dispatch('toggleFlip'),
    toggleInvert: () => dispatch('toggleInvert'),
    rotateLeft: () => dispatch('rotateLeft'),
    rotateRight: () => dispatch('rotateRight'),
    increaseContrast: () => dispatch('increaseContrast'),
    decreaseContrast: () => dispatch('decreaseContrast'),
    reset: () => dispatch('reset')
  }
}

export type ImageAdjustmentsControls = ReturnType<typeof useImageAdjustments>

export default useImageAdjustments
